import { useState, useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';
import './App.scss';
import Projects from './components/Projects';
import Title from './components/Title';
import Nav from './components/Nav';
import Footer from './components/Footer';
import Hero from './components/Hero';
import About from './components/About';
import Education from './components/Education';
import Contact from './components/Contact';
import Experience from './components/Experience';
import ProjectOverlay from './components/ProjectOverlay';
import ProjectHeader from './components/ProjectHeader';
import { ScrollProvider } from './context/ScrollContext';
import { useScrollParallax } from './hooks/useScrollTrigger';
import { getProjectBySlug, isValidProjectSlug } from './data/projects';

function App() {
  const [activePage, setActivePage] = useState("home");
  const [viewingProject, setViewingProject] = useState(null);

  // Element refs
  const mainContainer = useRef(null);
  const infoContainer = useRef(null);
  const heroRef = useRef(null);
  const aboutRef = useRef(null);
  const educationRef = useRef(null);
  const projectsRef = useRef(null);

  // Animation refs
  const navRef = useRef(null);
  const titleRef = useRef(null);
  const projectHeaderRef = useRef(null);
  const timelineRef = useRef(null);

  // Hero parallax as about section scrolls into view
  useScrollParallax(heroRef, {
    to: { y: 500 },
    triggerRef: aboutRef,
    start: 'top bottom',
    end: 'top+=200 top',
  }, infoContainer);

  // Initialize GSAP timeline (only on mount and resize)
  useEffect(() => {
    if (!navRef.current || !titleRef.current || !projectHeaderRef.current) {
      return;
    }

    const MOBILE_BREAKPOINT = 600;

    const buildTimeline = () => {
      const wasPlayed = timelineRef.current?.progress() === 1;

      if (timelineRef.current) {
        timelineRef.current.kill();
      }

      const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      const tl = gsap.timeline({ paused: true });

      if (isMobile) {
        tl.to(navRef.current, {
          y: 'calc(100% + 1em)',
          height: 0,
          padding: 0,
          opacity: 0,
          duration: 0.4
        }, 0);
      } else {
        tl.to(navRef.current, {
          x: '-100%',
          width: 0,
          minWidth: 0,
          padding: 0,
          opacity: 0,
          duration: 0.4
        }, 0);
      }

      tl.to(titleRef.current, { y: 50, opacity: 0, duration: 0.3 }, 0)
        .fromTo(
          projectHeaderRef.current,
          { y: 50, opacity: 0 },
          { opacity: 1, y: 0, duration: 0.4, onStart: () => {
            projectHeaderRef.current.classList.add('visible');
          }},
          0.1
        );

      timelineRef.current = tl;

      if (wasPlayed) {
        tl.progress(1);
      }
    };

    buildTimeline();

    window.addEventListener('resize', buildTimeline);
    return () => {
      window.removeEventListener('resize', buildTimeline);
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
    };
  }, []);

  // URL sync on initial load
  useEffect(() => {
    const path = window.location.pathname.slice(1);
    if (path && isValidProjectSlug(path)) {
      setViewingProject(path);
    }

    const handlePopState = () => {
      const newPath = window.location.pathname.slice(1);
      if (newPath && isValidProjectSlug(newPath)) {
        setViewingProject(newPath);
      } else {
        setViewingProject(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Animate on viewingProject change
  useEffect(() => {
    if (!timelineRef.current) return;

    if (viewingProject) {
      // Scroll to top for project view
      if (infoContainer.current) {
        infoContainer.current.scrollTop = 0;
      }
      timelineRef.current.play();
    } else {
      timelineRef.current.reverse().then(() => {
        projectHeaderRef.current?.classList.remove('visible');
      });
    }
  }, [viewingProject]);

  const handleSelectProject = useCallback((slug) => {
    setViewingProject(slug);
    window.history.pushState({}, '', `/${slug}`);
  }, []);

  const handleBackToProjects = useCallback(() => {
    setViewingProject(null);
    window.history.pushState({}, '', '/');
    // Scroll to projects section instantly after React re-renders the content
    setTimeout(() => {
      const projectsElement = document.getElementById('projects');
      if (projectsElement) {
        projectsElement.scrollIntoView({ behavior: 'instant' });
      }
    }, 50);
  }, []);

  useEffect(() => {
    let mainCopy = mainContainer.current;
    mainCopy.addEventListener('scroll', checkScroll, true);
    return () => {
      mainCopy.removeEventListener("scroll", checkScroll, true);
    };
  }, []);

  const checkScroll = () => {
    // Skip scroll check when viewing a project (elements not rendered)
    if (!heroRef.current || !aboutRef.current || !educationRef.current || !projectsRef.current) {
      return;
    }
    const boundingBoxHero = heroRef.current.getBoundingClientRect();
    const boundingBoxAbout = aboutRef.current.getBoundingClientRect();
    const boundingBoxEducation = educationRef.current.getBoundingClientRect();
    const boundingBoxProjects = projectsRef.current.getBoundingClientRect();

    if (boundingBoxHero.top < mainContainer.current.getBoundingClientRect().top &&
      boundingBoxHero.bottom - 215 > mainContainer.current.getBoundingClientRect().top &&
      heroRef.current) {
      setActivePage("home");
    }
    else if (boundingBoxAbout.top < mainContainer.current.getBoundingClientRect().top &&
      boundingBoxAbout.bottom - 70 > mainContainer.current.getBoundingClientRect().top &&
      aboutRef.current) {
      setActivePage("about");
    }
    else if (boundingBoxEducation.top - 70 < mainContainer.current.getBoundingClientRect().top &&
      boundingBoxEducation.bottom > mainContainer.current.getBoundingClientRect().top &&
      educationRef.current) {
      setActivePage("education");
    }
    else if (boundingBoxProjects.top - 150 < mainContainer.current.getBoundingClientRect().top &&
      boundingBoxProjects.bottom > mainContainer.current.getBoundingClientRect().top &&
      projectsRef.current) {
      setActivePage("projects");
    }
  };

  const currentProject = viewingProject ? getProjectBySlug(viewingProject) : null;

  return (
    <div className="App">
      <Nav ref={navRef} activePage={activePage} setActivePage={setActivePage} hidden={!!viewingProject} />
      <div className={`mainContainer ${activePage} ${viewingProject ? 'projectView' : ''}`} ref={mainContainer}>
        <Title ref={titleRef} activePage={activePage} hidden={!!viewingProject} />
        <ProjectHeader
          ref={projectHeaderRef}
          projectTitle={currentProject?.title || ''}
          onBack={handleBackToProjects}
        />
        <ScrollProvider scrollerRef={infoContainer}>
          <div className='infoContainer'>
            <div className='infoContainerInner' ref={infoContainer}>
              {!viewingProject ? (
                <>
                  <Hero heroRef={heroRef} />
                  <About aboutRef={aboutRef} />
                  <Education educationRef={educationRef} />
                  <Experience />
                  <Projects projectsRef={projectsRef} onSelectProject={handleSelectProject} />
                  <Contact />
                </>
              ) : (
                <ProjectOverlay project={currentProject} />
              )}
            </div>
          </div>
        </ScrollProvider>
        <Footer />
      </div>
    </div>
  );
}

export default App;
